/**
 * Voice AI — IVR Grid
 * Card grid with search, filters, skeleton loading, and empty state.
 * Dispatches: select-menu, create-menu, search, filter
 * Pattern: agent-grid.js
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

export class IvrGrid extends LitElement {
  static properties = {
    menus: { type: Array },
    loading: { type: Boolean },
    viewMode: { type: String },
    searchQuery: { type: String },
    statusFilter: { type: String },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.menus = [];
    this.loading = true;
    this.viewMode = localStorage.getItem('voice-ivr-view') || 'grid';
    this.searchQuery = '';
    this.statusFilter = '';
    this._searchDebounce = null;
  }

  _onSearchInput(e) {
    const val = e.target.value;
    clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => {
      this.dispatchEvent(new CustomEvent('search', { detail: { query: val } }));
    }, 300);
  }

  _setStatusFilter(status) {
    this.dispatchEvent(new CustomEvent('filter', { detail: { status } }));
  }

  _selectMenu(menu) {
    this.dispatchEvent(new CustomEvent('select-menu', { detail: { menu } }));
  }

  _createMenu() {
    this.dispatchEvent(new CustomEvent('create-menu'));
  }

  _setViewMode(mode) {
    this.viewMode = mode;
    localStorage.setItem('voice-ivr-view', mode);
  }

  _statusBadge(status) {
    const map = {
      active: { cls: 'badge-success', dot: 'active', text: 'Active' },
      inactive: { cls: 'badge-neutral', dot: 'inactive', text: 'Inactive' },
      draft: { cls: 'badge-warning', dot: 'warning', text: 'Draft' },
    };
    const b = map[status] || map.draft;
    return html`
      <span class="badge ${b.cls}">
        <span class="status-dot ${b.dot}"></span>
        ${b.text}
      </span>
    `;
  }

  render() {
    return html`
      ${iconStyles}
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">IVR Menus</h1>
          <p class="page-description">Configure interactive voice response menus for inbound calls</p>
        </div>
        <div class="header-actions">
          <div class="view-toggle">
            <button
              class="view-toggle-btn ${this.viewMode === 'grid' ? 'active' : ''}"
              @click=${() => this._setViewMode('grid')}
              title="Grid view"
            >
              <i class="fa-sharp fa-light fa-grid-2"></i>
            </button>
            <button
              class="view-toggle-btn ${this.viewMode === 'list' ? 'active' : ''}"
              @click=${() => this._setViewMode('list')}
              title="List view"
            >
              <i class="fa-sharp fa-light fa-list"></i>
            </button>
          </div>
          <button class="btn btn-primary" @click=${this._createMenu}>
            <i class="fa-sharp fa-light fa-plus"></i>
            Create Menu
          </button>
        </div>
      </div>

      <div class="search-filters">
        <div class="search-input-wrap">
          <i class="fa-sharp fa-light fa-magnifying-glass"></i>
          <input
            type="text"
            class="search-input"
            placeholder="Search menus..."
            .value=${this.searchQuery}
            @input=${this._onSearchInput}
          />
        </div>
        <div class="filter-pills">
          <button class="filter-pill ${this.statusFilter === '' ? 'active' : ''}" @click=${() => this._setStatusFilter('')}>All</button>
          <button class="filter-pill ${this.statusFilter === 'active' ? 'active' : ''}" @click=${() => this._setStatusFilter('active')}>Active</button>
          <button class="filter-pill ${this.statusFilter === 'draft' ? 'active' : ''}" @click=${() => this._setStatusFilter('draft')}>Draft</button>
          <button class="filter-pill ${this.statusFilter === 'inactive' ? 'active' : ''}" @click=${() => this._setStatusFilter('inactive')}>Inactive</button>
        </div>
        ${!this.loading ? html`<span class="results-count">${this.menus.length} menu${this.menus.length !== 1 ? 's' : ''}</span>` : ''}
      </div>

      ${this.loading ? html`
        <div class="ivr-grid ${this.viewMode === 'list' ? 'list-view' : ''}">
          ${[1, 2, 3].map(() => this.viewMode === 'list'
            ? this._renderSkeletonList()
            : this._renderSkeleton()
          )}
        </div>
      ` : this.menus.length === 0 ? html`
        <div class="empty-state">
          <div class="empty-state-icon"><i class="fa-sharp fa-light fa-diagram-project"></i></div>
          <div class="empty-state-title">${this.searchQuery || this.statusFilter ? 'No matching menus' : 'No IVR menus yet'}</div>
          <div class="empty-state-description">
            ${this.searchQuery || this.statusFilter
              ? 'Try adjusting your search or filters.'
              : 'Create your first IVR menu to route inbound calls with interactive options.'}
          </div>
          ${!this.searchQuery && !this.statusFilter ? html`
            <button class="btn btn-primary" style="margin-top:var(--space-4);" @click=${this._createMenu}>
              <i class="fa-sharp fa-light fa-plus"></i>
              Create Menu
            </button>
          ` : ''}
        </div>
      ` : html`
        <div class="ivr-grid ${this.viewMode === 'list' ? 'list-view' : ''}">
          ${this.menus.map(menu => this._renderCard(menu))}
        </div>
      `}
    `;
  }

  _renderSkeleton() {
    return html`
      <div class="ivr-card sk-card">
        <div class="ivr-card-header">
          <div class="skeleton-line circle"></div>
          <div class="ivr-info">
            <div class="skeleton-line" style="width:65%;height:16px;margin-bottom:8px;"></div>
            <div style="display:flex;gap:var(--space-2);">
              <div class="skeleton-line" style="width:56px;height:20px;border-radius:9999px;"></div>
            </div>
          </div>
        </div>
        <div class="ivr-stats-row">
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:50%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:70%;"></div>
          </div>
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:45%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:75%;"></div>
          </div>
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:40%;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:60%;"></div>
          </div>
        </div>
        <div class="ivr-card-footer">
          <div class="skeleton-line" style="width:45%;height:12px;"></div>
          <div class="skeleton-line" style="width:25%;height:12px;"></div>
        </div>
      </div>
    `;
  }

  _renderSkeletonList() {
    return html`
      <div class="ivr-card sk-card">
        <div class="ivr-card-header" style="margin-bottom:0;">
          <div class="skeleton-line circle"></div>
          <div class="ivr-info">
            <div class="skeleton-line" style="width:120px;height:16px;margin-bottom:6px;"></div>
            <div style="display:flex;gap:var(--space-2);">
              <div class="skeleton-line" style="width:56px;height:18px;border-radius:9999px;"></div>
            </div>
          </div>
        </div>
        <div class="ivr-stats-row" style="margin-bottom:0;">
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:36px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:60px;"></div>
          </div>
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:32px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:70px;"></div>
          </div>
          <div class="ivr-stat-item">
            <div class="skeleton-line" style="width:28px;height:16px;margin-bottom:4px;"></div>
            <div class="skeleton-line xs" style="width:55px;"></div>
          </div>
        </div>
        <div class="ivr-card-footer" style="border-top:none;padding-top:0;">
          <div class="skeleton-line" style="width:100px;height:12px;margin-bottom:4px;"></div>
          <div class="skeleton-line" style="width:70px;height:12px;"></div>
        </div>
      </div>
    `;
  }

  _renderCard(menu) {
    return html`
      <div class="ivr-card" @click=${() => this._selectMenu(menu)}>
        <div class="ivr-card-header">
          <div class="ivr-icon ${menu.status || 'draft'}">
            <i class="fa-sharp fa-light fa-diagram-project"></i>
          </div>
          <div class="ivr-info">
            <div class="ivr-name">${menu.name}</div>
            <div class="ivr-meta">
              ${this._statusBadge(menu.status)}
            </div>
          </div>
        </div>

        <div class="ivr-stats-row">
          <div class="ivr-stat-item">
            <span class="stat-value">${menu.optionCount}</span>
            <span class="stat-label">Options</span>
          </div>
          <div class="ivr-stat-item">
            <span class="stat-value">${menu.phoneCount}</span>
            <span class="stat-label">Routes</span>
          </div>
          <div class="ivr-stat-item">
            <span class="stat-value">${menu.scheduleCount}</span>
            <span class="stat-label">Schedules</span>
          </div>
        </div>

        <div class="ivr-card-footer">
          <span class="footer-desc">${menu.description || 'No description'}</span>
          <span class="footer-voice">
            <i class="fa-sharp fa-light fa-waveform-lines" style="font-size: 10px;"></i>
            ${menu.voiceName || 'Polly.Joanna'}
          </span>
        </div>
      </div>
    `;
  }
}

customElements.define('ivr-grid', IvrGrid);
